import React, { useState, useEffect, useCallback } from 'react'
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import { Fetch } from '@/utils/Fetch'

import { EmailPreviewModalPropsType } from '../../types'

const SIGN_URL_PLACEHOLDER = '{{sign_url}}'

export const EmailPreviewModal: React.FC<EmailPreviewModalPropsType> = ({
  show,
  setShow,
  customerContract,
  refreshCustomerContract,
  resendTo,
}) => {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [bodyError, setBodyError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadPreview = useCallback(async () => {
    setIsLoading(true)
    const res = await Fetch(
      `/api/v1/customer_contracts/${customerContract.id}/email_preview?email_type=signature_request`,
      'GET',
      {},
    )
    if (res.ok) {
      const json = await res.json()
      setSubject(json.subject || '')
      setBody(json.body || '')
      setBodyError('')
    } else {
      alert('メールプレビューの取得に失敗しました')
    }
    setIsLoading(false)
  }, [customerContract.id])

  useEffect(() => {
    if (show) loadPreview()
  }, [show, loadPreview])

  const handleBodyChange = (value: string) => {
    setBody(value)
    if (bodyError && value.includes(SIGN_URL_PLACEHOLDER)) setBodyError('')
  }

  const submitEmail = useCallback(async () => {
    if (!body.includes(SIGN_URL_PLACEHOLDER)) {
      setBodyError(`本文に署名URL ${SIGN_URL_PLACEHOLDER} が含まれていません。必ず含めてください`)
      return
    }
    setIsSubmitting(true)
    const res = resendTo
      ? await Fetch(`/api/v1/contract_invitees/${resendTo.contractInviteeId}/signature_request`, 'POST', {
          signature_request: { invitation_subject: subject, invitation_message: body },
        })
      : await Fetch(`/api/v1/customer_contracts/${customerContract.id}`, 'PATCH', {
          customer_contract: { status: 'pending', invitation_subject: subject, invitation_message: body },
        })
    if (res.ok) {
      setShow(false)
      if (resendTo) {
        resendTo.onResent()
      } else {
        refreshCustomerContract?.()
      }
    } else {
      const json = await res.json()
      const errorMessage = json.errors?.join('\n')
      alert(errorMessage || (resendTo ? '再送に失敗しました' : '送信に失敗しました'))
    }
    setIsSubmitting(false)
  }, [customerContract.id, subject, body, resendTo, refreshCustomerContract])

  return (
    <Modal show={show} onHide={() => setShow(false)} scrollable size="lg">
      <Modal.Header closeButton>{resendTo ? '署名依頼メールの再送' : '送信内容の確認'}</Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-4">読み込み中...</div>
        ) : (
          <Form>
            {resendTo && (
              <Form.Group className="mb-3">
                <Form.Label>宛先</Form.Label>
                <div>
                  {resendTo.inviteeName} 様（{resendTo.inviteeEmail}）
                </div>
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>件名</Form.Label>
              <Form.Control type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
            </Form.Group>
            <Form.Group>
              <Form.Label>本文</Form.Label>
              <Form.Control
                as="textarea"
                rows={12}
                value={body}
                onChange={(e) => handleBodyChange(e.target.value)}
                isInvalid={!!bodyError}
              />
              <Form.Control.Feedback type="invalid">{bodyError}</Form.Control.Feedback>
              <Form.Text className="text-muted">
                ※ <code>{SIGN_URL_PLACEHOLDER}</code>{' '}
                は送信時に署名ページのURLに置き換わります。位置は変えて構いませんが、必ず本文に残してください。削除すると送信できません。
              </Form.Text>
            </Form.Group>
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" type="button" onClick={() => setShow(false)} disabled={isSubmitting}>
          キャンセル
        </Button>
        <Button variant="success" type="button" onClick={submitEmail} disabled={isLoading || isSubmitting}>
          {resendTo ? (isSubmitting ? '再送中' : '再送する') : isSubmitting ? '送信中' : '送信する'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
